import "../index.css";

import App from "./App.tsx";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";

import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";

import Layout from "./Layout.tsx";

import Login from "@/console/pages/Login.tsx";
import Dashboard from "@/console/pages/Dashboard/page.tsx";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer, toast as reactToast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { I18nextProvider } from "react-i18next";
import i18n from "../console/i18n.ts";

const Members = lazy(async () => await import("@/console/pages/Members/page.tsx"));

const MembersDetail = lazy(async () => await import("@/console/pages/Members/components/MemberDetail.tsx"));
const FrontDesk = lazy(async () => await import("@/console/pages/FrontDesk/page.tsx"));
const Account = lazy(async () => await import("@/console/pages/Account/page.tsx"));
const AppManagement = lazy(async () => await import("@/console/pages/AppManagement/page.tsx"));
const Schedule = lazy(async () => await import("@/console/pages/Schedule/page.tsx"));
const GymInfo = lazy(async () => await import("@/console/pages/GymInfo/page.tsx"));
const GymClass = lazy(async () => await import("@/console/pages/GymClass/page.tsx"));
const Manager = lazy(async () => await import("@/console/pages/Manager/page.tsx"));
const ManagerDetail = lazy(async () => await import("@/console/pages/Manager/components/ManagerDetail.tsx"));
const Inquiry = lazy(async () => await import("@/console/pages/Inquiry/page.tsx"));
const Attendance = lazy(async () => await import("@/console/pages/Attendance/page.tsx"));
const AttendanceManager = lazy(async () => await import("@/console/pages/AttendanceManager/page.tsx"));

import { errorMap } from "@/constants/errorMap.ts";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 1000 * 60 * 5,
    },
    mutations: {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      onError: (error: any) => {
        const errorCode = Number(error.response.data.error);
        reactToast.error(errorMap[errorCode]);
      },
    },
  },
});

window.toast = reactToast;

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<App />}>
      <Route path="login" element={<Login />} />
      <Route element={<Layout />}>
        <Route index path="dashboard" element={<Dashboard />} />
        <Route path="members" element={<Members />}>
          <Route path=":memberId" element={<MembersDetail />} />
        </Route>
        <Route path="attendance" element={<Attendance />} />
        <Route path="attendance-manager" element={<AttendanceManager />} />
        <Route path="account" element={<Account />} />
        <Route path="app-manager" element={<AppManagement />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="gym-info" element={<GymInfo />} />
        <Route path="gym-class" element={<GymClass />} />
        <Route path="manager" element={<Manager />}>
          <Route path=":managerId" element={<ManagerDetail />} />
        </Route>
        <Route path="inquiry" element={<Inquiry />} />
      </Route>

      <Route
        path="front-desk"
        element={
          <Suspense fallback={null}>
            <FrontDesk />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>,
  ),
);

// biome-ignore lint/style/noNonNullAssertion: <explanation>
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
